import { useEffect } from "react";
import { ExpandOutlined } from "@ant-design/icons";
import { clockOf } from "./rules/format";
import { useReviewClock } from "./clock";
import Shot from "./Shot";

const sevClass = (x) => (x.flagged ? ` proctoring-review__shot--${x.severity}` : "");

/*
 * A round with no recording: its camera captures where the player would be.
 * The one in focus is the one picked or, for any other moment, the last taken
 * before it. A click picks a capture; a second click enlarges it.
 */
export default function SnapshotReel({ captures, sel, imageOf, onImageError, moduleAt, onPick, onZoom }) {
  const c = useReviewClock();
  const real = captures.filter((x) => x.capture !== "reference");
  const flaggedN = real.filter((x) => x.flagged).length;
  const picked = captures.find((x) => x.key === sel) || null;
  const focus = picked || (c.engaged ? captures.reduce((a, x) => (x.at <= c.t ? x : a), null) : null);
  const focusKey = focus ? focus.key : null;

  useEffect(() => {
    if (!focusKey) return;
    const node = Array.from(document.querySelectorAll(".proctoring-review [data-shot]")).find((n) => n.getAttribute("data-shot") === focusKey);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusKey]);

  const where = (x) => {
    const m = moduleAt(x.at);
    return m ? m.name : "";
  };

  return (
    <div className="proctoring-review__reel" id="proctoring-review-player" data-source="images">
      <div className="proctoring-review__reel-head">
        <b>Captures</b>
        <span>{`Not recorded. ${real.length} ${real.length === 1 ? "capture" : "captures"}${flaggedN ? `, ${flaggedN} flagged` : ""}.`}</span>
      </div>
      {focus && (
        <div className="proctoring-review__reel-focus">
          <button type="button" className={`proctoring-review__ev-big${sevClass(focus)}`} onClick={() => onZoom(focus.key)} aria-label="Enlarge the capture">
            <Shot url={imageOf(focus)} alt={focus.what} onError={onImageError} />
            <span className="proctoring-review__ev-zoom"><ExpandOutlined /> Enlarge</span>
          </button>
          <span>
            <b>{focus.what}</b>
            {`${clockOf(focus.at)}${where(focus) ? ` · ${where(focus)}` : ""}${focus.questionLabel ? ` · ${focus.questionLabel}` : ""}`}
            {!picked && <small>{`The capture before ${clockOf(c.t)}`}</small>}
            {focus.detail && <small>{focus.detail}</small>}
          </span>
        </div>
      )}
      <div className="proctoring-review__reel-grid">
        {captures.map((x) => (
          <button
            key={x.key}
            type="button"
            data-shot={x.key}
            className={`proctoring-review__shot${sevClass(x)}${focusKey === x.key ? " is-on" : ""}`}
            onClick={() => (sel === x.key ? onZoom(x.key) : onPick(x))}
            title={`${clockOf(x.at)} ${x.what}${sel === x.key ? " · click to enlarge" : ""}`}
            aria-label={`${clockOf(x.at)} ${x.what}`}
          >
            <Shot url={imageOf(x)} alt="" onError={onImageError} />
            <span>{x.capture === "reference" ? "Reference" : clockOf(x.at)}<small>{where(x)}</small></span>
          </button>
        ))}
      </div>
    </div>
  );
}
